import React from "react";

import { FLOW_TYPES } from "../../constants/flowType";
import { useTicketingContext } from "../../store";
import { HELPERS, VISUAL_HELPERS } from "../../utils";
import UserAvatar from "../UserAvatar/UserAvatar";
import { Button, CarlsbergLogo, Select } from "./../../components";

const SummaryView = ({
  onContinue,
  onBack,
  admissionTickets,
  isActiveStepValid,
  onCurrencySelect,
  dateText,
  isSummaryEmpty,
  totals,
  isFirstStep,
  isLastStep,
  isCartUpdateInProgress,
}) => {
  const { state } = useTicketingContext();

  const onChangeHandler = (value) => {
    onCurrencySelect(value);
  };

  const { addons, currencies, dates, texts, summary } = state;

  const {
    selectedAdmissionTickets,
    selectedAddonTickets,
    selectedTimeslot,
    selectedCurrency,
    selectedDate,
    selectedGiftCardVariants,
  } = summary;

  const giftCardVariantList = selectedGiftCardVariants
    ? Object.values(selectedGiftCardVariants)
    : [];

  const sortedSelectedAdmissionTickets = selectedAdmissionTickets?.sort(
    (a, b) => a.ordinal - b.ordinal
  );

  const isActivitiesOnlyFlow = state.flowType === FLOW_TYPES.ACTIVITIES_ONLY;
  const isGiftCardFlow = state.flowType === FLOW_TYPES.GIFT_CARD;

  const showBackButton =
    !isFirstStep && state.flowType === FLOW_TYPES.ADMISSION;

  const hideLoginButton = state.is_city_card;

  return (
    <div className="summary-main">
      <div className="summary-main-logo">
        <CarlsbergLogo color="white" width="125px" />
        {!hideLoginButton ? (
          <UserAvatar className="summary-main-logo-avatar" />
        ) : null}
      </div>
      <div className="summary-main-title">
        <h1>{texts.summary.title}</h1>
        <Select
          handleOnChange={onChangeHandler}
          id="currency"
          options={currencies.map(({ id, name }) => ({
            label: name,
            value: id,
          }))}
          style={{ position: "absolute", right: "60px", zIndex: 1 }}
        />
      </div>

      {/* --------------- GIFT CARD LINES ---------------  */}
      <div className="summary-tickets-container">
        {isGiftCardFlow && giftCardVariantList.length > 0 && (
          <div className="summary-tickets">
            <div className="summary-tickets-header">
              <p>Gift Card</p>
              <span />
            </div>
            <ul className="tickets-list">
              {giftCardVariantList.map((variant) => (
                <li className="tickets-list-item" key={variant.id}>
                  <p>{`${variant.qty || 1} × ${variant.title} ${
                    variant.price.currencyCode
                  }`}</p>
                  <p>
                    <span>{`${
                      selectedCurrency.name
                    } ${VISUAL_HELPERS.displayPriceBasedOnCurrency(
                      Number(variant.price.amount) * (variant.qty || 1),
                      selectedCurrency
                    )}`}</span>
                  </p>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {/* --------------- ADMISSION TICKETS LIST ---------------  */}
      <div className="summary-tickets-container">
        {selectedAdmissionTickets.length > 0 && (
          <div className="summary-tickets">
            <div className="summary-tickets-header">
              <p>{texts.summary.admission_tickets}</p>
              <span className="summary-date">
                {dateText}{" "}
                {selectedTimeslot != null
                  ? `- ${VISUAL_HELPERS.displayTimeString(
                      HELPERS.getTimestampMillisBasedOnEntryNo(
                        dates,
                        selectedDate,
                        selectedTimeslot
                      )
                    )}`
                  : null}
              </span>
            </div>
            <ul className="tickets-list">
              {sortedSelectedAdmissionTickets.map((ticket) => {
                const { id, qty } = ticket;
                const name = HELPERS.getAdmissionTicketNameById(
                  id,
                  admissionTickets
                );
                const { min, max } = HELPERS.getPriceOfAdmissionTicketById(
                  id,
                  admissionTickets,
                  selectedDate,
                  dates
                );
                const freeText = texts.common.free;
                const isIndicativeCalculation = selectedDate === null;
                const totalPriceString = VISUAL_HELPERS.displayPriceText(
                  min,
                  max,
                  selectedCurrency,
                  freeText,
                  qty,
                  isIndicativeCalculation
                );
                const pricePerPerson = VISUAL_HELPERS.displayPricePerPersonText(
                  min,
                  max,
                  selectedCurrency,
                  freeText,
                  qty,
                  isIndicativeCalculation
                );
                const showPerPeson = pricePerPerson !== freeText;
                return (
                  <li className="tickets-list-item" key={id}>
                    <p>{`${qty} ${name}`}</p>
                    <p>
                      <span>{totalPriceString}</span>
                      {showPerPeson ? (
                        <span className="description">{`${pricePerPerson} ${texts.common.per_person}`}</span>
                      ) : null}
                    </p>
                  </li>
                );
              })}
            </ul>
          </div>
        )}

        {/* --------------- ADDON TICKETS LIST ---------------  */}
        {Object.keys(selectedAddonTickets).length > 0 && (
          <div className="summary-tickets">
            <div className="summary-tickets-header">
              <p>
                {isActivitiesOnlyFlow
                  ? texts.summary?.addon_events_tickets
                  : texts.summary.addon_tickets}
              </p>
              <span />
            </div>
            <ul className="tickets-list">
              {Object.keys(selectedAddonTickets).map((addonId) => {
                const title = HELPERS.getAddonEventTitleById(addons, addonId);

                const ticketInfoText =
                  VISUAL_HELPERS.displayAddonTicketInfoText(
                    addons,
                    addonId,
                    selectedAddonTickets[addonId],
                    selectedDate,
                    texts,
                    isActivitiesOnlyFlow
                  );

                let pricePerPerson, totalPrice;

                const prices = totals.addonsTotalPrices.find(
                  (total) => total.addonId === addonId
                );

                if (prices) {
                  pricePerPerson = prices.pricePerPerson;
                  totalPrice = prices.totalPrice;
                }

                return (
                  <li className="tickets-list-item" key={addonId}>
                    <p>
                      <span>{title}</span>
                      <span className="description">{ticketInfoText}</span>
                    </p>
                    <p>
                      <span>{`${
                        selectedCurrency.name
                      } ${VISUAL_HELPERS.displayPriceBasedOnCurrency(
                        totalPrice,
                        selectedCurrency
                      )}`}</span>
                      <span className="description">{`${
                        selectedCurrency.name
                      } ${VISUAL_HELPERS.displayPriceBasedOnCurrency(
                        pricePerPerson,
                        selectedCurrency
                      )} ${texts.common.per_person}`}</span>
                    </p>
                  </li>
                );
              })}
            </ul>
          </div>
        )}
      </div>
      {/* --------------- SUMMARY OF SUMMARY ---------------  */}

      <div className="totals-container">
        {isSummaryEmpty ? (
          <p className="summary-empty-info">{texts.summary.no_items}</p>
        ) : null}
        {!isSummaryEmpty && (
          <div className="totals">
            <div className="totals-total">
              <p>{texts.summary.total}</p>
              <p>{`${
                selectedCurrency.name
              } ${VISUAL_HELPERS.displayPriceBasedOnCurrency(
                totals.totalWithDiscount,
                selectedCurrency
              )}`}</p>
            </div>
          </div>
        )}
        <div
          className={
            !showBackButton ? "button-group single-button" : "button-group"
          }
        >
          {showBackButton ? (
            <Button
              color="secondary"
              fullWidth={false}
              label={texts.buttons.back}
              onClick={onBack}
            />
          ) : null}

          {isLastStep ? (
            <Button
              color="primary"
              disabled={isCartUpdateInProgress || !isActiveStepValid}
              fullWidth={false}
              label={texts.buttons.check_out}
              onClick={onContinue}
              showSpinner={isCartUpdateInProgress}
            />
          ) : (
            <Button
              color="primary"
              disabled={!isActiveStepValid}
              fullWidth={false}
              label={texts.buttons.continue}
              onClick={onContinue}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default React.memo(SummaryView);
